// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import fetch from 'cross-fetch';
import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';
import axios from 'axios';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  if (typeof window !== 'undefined') {
    // Otherwise, use the same domain and port as the frontend
    return `${window.location.origin}`;
  }

  return process.env.REACT_APP_CANONICAL_ROOT_URL;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const handleRequest = (url, options) => {
  return fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return handleRequest(url, options);
};

const postWIthMultipart = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
   //  'Content-Type': 'multipart/form-data',
    },
    body: body,
  };
  return handleRequest(url, options);
};

const patch = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return handleRequest(url, options);
};

const get = (path, params = {}, headers) => {
  const url = `${apiBaseUrl()}${path}`;
  const queryParams = new URLSearchParams(params).toString();
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
      ...headers,
    },
  };
  return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

//
export const stockManagement = body => {
  const url = '/api/stock-management';
  return post(url, body);
};

export const getFandoms = body => {
  const url = '/api/fave-fandoms';
  return get(url);
};

export const getLevelForFandom = (fan, fandom) => {
  const url = `/api/fave-level-for-fandom?fan=${fan}&fandom=${fandom}`;
  return get(url);
};

export const getFandomsSearch = body => {
  const url = '/api/fave-fandoms-search';
  return get(url, { search: body });
};

export const convert = body => {
  const url = '/api/files/convert';
  console.log("yo", body);
  return postWIthMultipart(url, body);
};


export const getFandom = body => {
  const url = '/api/fave-fandom';
  return get(url, { fandomId: body });
};

export const verifyFaveEmail = token => {
  const url = '/api/fave-email-verify';
  return get(url, { verificationToken: token });
};

export const createDigitalGoodUrl = body => {
  const url = '/api/digital-goods/create-digital-good-url';
  return post(url, body);
};

export const updateDigitalGoodUrl = body => {
  const url = `/api/digital-goods/update-digital-good-url`;
  return patch(url, body);
};

export const getDigitalGoodUrl = (listingId, transactionId, userId) => {
  const url = `/api/digital-goods/get-digital-good-url?listingId=${listingId}&transactionId=${transactionId}&userId=${userId}`;
  return get(url);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const integrationListings = params => {
  const url = `${apiBaseUrl()}/api/flex-integration-sdk`;
  return axios.post(url, null, { params: params });
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const logoutAuth0 = () => {
  return get('/api/auth/auth0/logout');
};

export const loginFirebase = body => {
  return post('/api/auth/firebase', body);
};

// PayPal
const paypalOnboardingLink = () => {
  return get('/api/paypal/onboarding-links');
};

const paypalMerchantOnboardingStatus = () => {
  return get('/api/paypal/merchant-signup-status');
};

export const paypalCreateOrder = body => {
  return post('/api/paypal/create-order', body);
};

export const paypalCaptureOrder = body => {
  return post('/api/paypal/capture-order', body);
};

export const initiatePrivilegedWithPaypal = body => {
  return post('/api/paypal/initiate-privileged', body);
};

export const transitionPrivilegedWithPaypal = body => {
  return post('/api/paypal/transition-privileged', body);
};

export const PayPalApi = {
  getOnboardingLink: paypalOnboardingLink,
  getMerchantOnboardingStatus: paypalMerchantOnboardingStatus,
  createOrder: paypalCreateOrder,
  captureOrder: paypalCaptureOrder,
};
